// Desk-account conversion — one of the desk's own accounts, some of what it
// holds into another asset (12 Sept 2026, evening).
//
// Operator: "give me swap screen, for all customer/treasury all accounts on
// left side tab — to convert their balances." A PARTY's box converts on
// account (ConvertBalance.tsx: an agreement, nothing moves). A DESK account
// is different: the office's AED does not become USDT by agreement — somebody
// took the AED and gave the USDT, a money changer, an exchange desk, a
// broker. So it IS a settled trade with that counterparty, and it is booked
// as one through POST /api/trades, exactly the body Book trade sends, with
// the sides shaped so the books take them (bff/src/routes/trades.ts
// deriveInventoryLeg: the coin is the traded side and the currency its money;
// a currency traded for a coin is refused as sides_swapped):
//
//   From a coin (whatever To)         → customer_buy   the desk sells the coin
//   From a currency, To a coin        → customer_sell  the desk buys the coin
//   From a currency, To a currency    → customer_buy   (no inventory book —
//                                                       the books warn and post)
//
// Whichever way, the counterparty TAKES the From asset off this account
// (amount_give · treasury_give_slug) and GIVES the To asset onto the account
// it lands on (amount_take · treasury_take_slug). Deal id, delivery method,
// the date door and the repeat guard follow NewTrade.tsx; the three boxes
// are the shared calculator, as on every screen with two assets and a rate.
// The payload's fields and their meaning are Book trade's — nothing new for
// the books to learn.
//
// Sweep of 14 Sept 2026 (SWEEP-SCOPE §5.9): the explaining hints went, the
// counterparty box reads "Counterparty · Search party", the chain-swap line
// points at Move money (the chain-swap instrument was designed out), Notes
// folded under More, one read-back sentence above the button — the direction
// and the "Lands on" default are derived and were never shown — and the band
// check is the shared table (lib/trade-math.ts).
//
// ── CONVERT ON AN EXCHANGE (operator, 15 Sept 2026: "fetch rates/fees data
//    from api & help me book the trades") ───────────────────────────────────
// When the From account is an exchange (treasury slug `exchange.*`), nobody
// took the money: the venue converted the desk's own USDC into BTC on the
// desk's own account. So the party IS the exchange — its own-account row,
// which the BFF accepts for exactly this shape (both sides on that exchange,
// settled) and posts rail → world → rail with no party box — the party
// picker is hidden and one line says "Converted on Bitget", and the To asset
// lands on the same exchange. The venue's own fills for the pair, from the
// last 3 days (GET /api/cex/fills), are offered as ONE button PER DUBAI DAY
// that presets the calculator; nothing moves without the tap. One evening's
// run is one conversion on the books (16 Sept 2026: Bitget bought 3.18 BTC on
// the 14th and 3.25 BTC on the 15th inside one window, and the two must never
// be offered as one figure). The posting carries the run's first and last fill
// (fills_from / fills_until), and the next read leaves every fill inside a
// booked window out — so the 15th can be booked before the 14th and the 14th
// is still offered. The tapped day's first fill is also offered as an As-of
// chip ("From the fills"), so one more tap dates the conversion at that run;
// the date changes only with that tap. The fee: with Venue fee
// (under More) EMPTY the preset takes the venue's fee inside the figures —
// From = cost + fee when the fee was in From, To = amount − fee when it was
// in To — so the two accounts read exactly what the exchange shows after one
// posting. With a Venue fee ENTERED the figures are the ones BEFORE the fee
// and the fee is recorded on its own, from that exchange's account, through
// the "Record the fee" link the success line carries (the trades route takes
// no cost field, so a separate expense is the one honest door). A fee typed
// after the tap re-presets the figures the other way, so the two can never
// double-count.
//
// 21 Oct 2026 audit (operator: "I need clean view for operator"): no From row
// (Swap.tsx's panel heading already names the account and its balance); the
// Party and amount boxes appear once To is chosen, not before (disabled and
// empty, they were noise); the default fee sentence under each fills button
// moved to About this screen; the As-of chips stand without a FieldRow of
// their own. Nothing that posts, enables or refuses changed.
//
// 22 Oct 2026 design-system sweep: a read that failed (the accounts, the parties, the desk's own accounts, the venue's fills) is
// one amber line with Details and Try again, never an empty list; the fills panel is a Label and one button per day (what it
// gives for what), the rate, fee and count the muted line under it; the two guards under a day (the deposit not booked yet, the
// dust over the books) and "has not held X before" are ONE line each with their figures behind Details; the read-back names no
// account the heading and the Lands-on box already name (16 words -> 9). Nothing that posts, enables or refuses changed.

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, type Direction, type CexFillSummary } from '../lib/api';
import { tg } from '../lib/telegram';
import { ASSETS, DEFAULT_RAILS, isFiat, kindsForCategory, derivePaymentMethod } from '../lib/workflow';
import { amountProblem, rateProblem, optionalAmountProblem } from '../lib/amount';
import { ApiError, isSentinel, nothingLanded } from '../lib/errors';
import { BALANCES_UNREAD, balancesUnread } from '../lib/balances-read';
import { useTradeMath } from '../hooks/useTradeMath';
import { bandWords } from '../lib/trade-math';
import { AssetPicker } from '../components/AssetPicker';
import { AsOfChips } from '../components/AsOfChips';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { Combobox, type ComboboxOption } from '../components/Combobox';
import { rosterCut } from '../lib/roster-cut';
import { Field, FieldRow } from '../components/Field';
import { ErrorLine, WarnLine } from '../components/ErrorLine';
import { MoreDetails } from '../components/MoreDetails';
import { Pending } from '../components/Pending';
import { RepeatPanel, useRepeatGuard, type RepeatKind } from '../components/RepeatPanel';
import { EmptyState, Label, ReadFailed, sayFirst, ServerLine, StateLine, TAP } from '../components/kit';
import type { Desk } from '../components/DeskSwitch';
import { partyName, partySubtitle, partyBoxWords, isStale, needsNameReview, railName, sameAsset, foldAsset, brandCase } from '../lib/naming';
import { ledgerText } from '../lib/ledger-scale';
import { fillsFrom, prefillOf, widened } from '../lib/convert-prefill';
import {
  displayAsset, fmtAmount, fmtQuantity, fmtDay, fmtRate, fmtRateAsGiven, fmtRateExact, dubaiDate,
  dubaiWallToIso, isoToDubaiWall, backdateProblem,
} from '../lib/format';

/** Map workflow's payment_method ('bank-transfer') to the BFF's delivery_method
 *  ('bank_transfer') — NewTrade's table, for the same field. */
const PM_TO_DM: Record<string, 'cash' | 'bank_transfer' | 'crypto' | undefined> = {
  cash: 'cash', 'bank-transfer': 'bank_transfer', crypto: 'crypto', other: undefined,
};

/** Can this rail take the To asset? It holds that exact code — USDT·TRC lands
 *  on a TRC wallet, never an ERC one — or the books' bare code for the same
 *  coin (books-v3 keeps the chain in the wallet name). */
const canLand = (rowAsset: string | undefined, to: string): boolean =>
  !!rowAsset && (rowAsset === to || (rowAsset === foldAsset(rowAsset) && sameAsset(rowAsset, to)));   // dialect-ok: exact chain code on purpose — the fold alone would land TRC coins on an ERC wallet

/** The exchange's BRAND NAME, for DISPLAY only — from the slug alone, so it
 *  reads before any data has loaded: `exchange.bitget` → 'bitget',
 *  `exchange.kucoin-india` → 'kucoin'. Empty for anything that is not an
 *  exchange.
 *
 *  NOT THE ACCOUNT (25 Sept 2026). The `-india` suffix used to be safe to
 *  strip because it named the entity and the venue stayed one account either
 *  way; the IndiaOTC wind-down put `exchange.bitget` and
 *  `exchange.bitget-india` on ONE desk together, so this word alone can no
 *  longer tell /api/cex/fills which account to answer for — both rails
 *  strip to 'bitget'. That needs `TreasuryRow.venue_account` (the resolved
 *  venue+label pair the BFF's own rail→account rule produces); see
 *  `venueAccount` below, used only by the fills query. */
const venueOfRail = (slug: string): string =>
  slug.startsWith('exchange.') ? (slug.split('.')[1] ?? '').replace(/-india$/, '') : '';

/** How far back the fills panel reads. */
const FILLS_DAYS = 3;

/** A day's rate as the venue's figures give it: its stored places, the whole part grouped — never rounded (rule 10; the
 *  table read 71,250.123456 as "71,250.1235", 4 Nov 2026). */
const fillRate = (rate: number | null): string => fmtRateAsGiven(fmtRateExact(rate));

/** A plain decimal for the calculator, AS THE BOOKS WILL HOLD IT: at the asset's own place (lib/ledger-scale.ts: the BFF's ASSET_SCALE),
 *  rounded half-even like the BFF's toMinor, trailing zeros gone, never an exponent. The venue's fills carry more places than the books
 *  keep (KuCoin's 2,50,019.95181425 USDC), and a figure finer than the ledger's scale would sit in the deal row while the posting rounds
 *  it: the box holds what the books will hold — rounded the way the books round, so that what is booked is what the BFF would have booked
 *  from the raw figure (a tie like 3.0000005 TRX is 3.000000, not 3.000001). */
const plain = ledgerText;

/** One day's run of the venue's fills for THIS pair, oriented From → To: the
 *  To amount, the From amount, the venue's rate and the fee. */
type PairFills = { g: CexFillSummary; toAmount: number; fromAmount: number; fee: number; feeAsset: string | null };

/** What the venue's fills say for THIS pair, one entry per Dubai day,
 *  earliest first — each evening's run is one conversion (header). Empty
 *  when the window holds no fills for the pair. The venue's pair is
 *  base/quote; the desk BOUGHT base when To is the base, SOLD it when From is. */
function fillsForPair(summary: CexFillSummary[], from: string, to: string): PairFills[] {
  const f = foldAsset(from), t = foldAsset(to);
  return summary
    .filter((s) => s.amount > 0 && ((s.symbol === `${t}/${f}` && s.side === 'buy') || (s.symbol === `${f}/${t}` && s.side === 'sell')))
    .sort((a, b) => (a.day ?? a.first_at ?? '').localeCompare(b.day ?? b.first_at ?? ''))   // an older Worker sends no day
    .map((g) => {
      const buy = g.side === 'buy';
      const [feeAsset, fee] = Object.entries(g.fees).find(([, v]) => v !== 0) ?? [null, 0];
      return { g, toAmount: buy ? g.amount : g.cost, fromAmount: buy ? g.cost : g.amount, fee, feeAsset };
    });
}

export function TreasurySwapForm({ entity, from }: { entity: Desk; from: RailValue }) {
  const nav = useNavigate();

  // The same key as Treasury, Book trade and the Convert list: one fetch, and
  // the refresh every posting triggers reaches it.
  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  const rows = treasury.data?.rows ?? [];
  const fromRow = useMemo(
    () => rows.find((r) => r.slug === from.slug && sameAsset(r.asset, from.asset)),
    [rows, from.slug, from.asset],
  );
  const balance = fromRow?.balance ?? 0;
  // A REFRESH THAT FAILED keeps the last good read beside the error (TanStack v5). The rows may stay on screen, but no claim is
  // drawn from them and nothing is posted off them (Appendix B): the over-balance warning, the dust guard, the "nothing held"
  // line and the Convert button all follow this one flag. Every posting refreshes ['treasury'], so this is the state a
  // successful conversion is most likely to meet — and the form, with the outcome and the guards it holds, stays through it.
  // The failure itself is said ONCE, by the Convert list's banner (Swap.tsx reads this same key and owns its Try again).
  const unknown = treasury.isError;
  const fromName = railName(fromRow ?? { slug: from.slug });

  // AN EXCHANGE — the venue itself converted the desk's money (header).
  const venue = venueOfRail(from.slug);
  const isExchange = venue !== '';
  // A BANK converting on its own account is the same shape (17 Sept 2026:
  // Thanos's 15,000 USD on ALUX · Wio became AED at the bank's rate — there
  // is no merchant, the bank is the party). Everything that says "the
  // account's own institution converted it" reads `selfConverts`; the fills
  // panel and the venue fee stay exchange-only.
  const isBank = from.slug.startsWith('bank.');
  const selfConverts = isExchange || isBank;
  const venueName = isExchange ? brandCase(venue) : fromName;

  // OPENED FROM AN UNBOOKED LINE on Convert's home (lib/convert-prefill.ts): its To asset, and the day to preset.
  const [search] = useSearchParams();
  const [pre] = useState(() => prefillOf(search, from.asset, ASSETS));
  const [to, setTo] = useState(pre.to);
  const [landsOn, setLandsOn] = useState<RailValue>({ slug: '', asset: '' });
  const [partySlug, setPartySlug] = useState('');
  const [notes, setNotes] = useState('');
  const [venueFee, setVenueFee] = useState('');
  const [venueFeeAsset, setVenueFeeAsset] = useState('');
  const [when, setWhen] = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  // The same coin on another chain is not a conversion — it is two ordinary
  // moves on Move money (the chain-swap instrument was designed out of the
  // fresh books; the BFF answers chain_swap_retired). So the picker excludes
  // the exact From code, not its fold: USDT·ERC stays pickable under a
  // USDT·TRC account so the line under the picker can say where that goes.
  const excludeFrom = (a: string) => a === from.asset;   // dialect-ok: exact chain code on purpose — the same coin on another chain must stay pickable
  const chainSwap = !!to && to !== from.asset && sameAsset(to, from.asset);   // dialect-ok: same fold, different chain code
  const pairKnown = !!to && !chainSwap;

  // WHERE THE TO ASSET LANDS. The rails that hold that asset (see canLand),
  // by kind as Book trade filters. Default: this same account when the books
  // know it holds the To asset (the office safe's INR beside its AED), else
  // the desk's everyday account for it — office cash for AED, the TRC hot
  // wallet for USDT·TRC — else nothing, and the operator picks.
  // ON AN EXCHANGE the To asset lands on that same exchange, always: a venue
  // conversion moves money between the exchange's own accounts and nowhere
  // else (the BFF refuses any other pair of sides). The account may never
  // have held the asset — the books then ask, in words, before opening it.
  const landRows = useMemo(() => (pairKnown ? rows.filter((r) => canLand(r.asset, to)) : []), [rows, to, pairKnown]);
  useEffect(() => {
    if (!pairKnown) { setLandsOn({ slug: '', asset: '' }); return; }
    if (selfConverts) { setLandsOn({ slug: from.slug, asset: to }); return; }
    const same = rows.find((r) => r.slug === from.slug && canLand(r.asset, to));
    if (same)                                            { setLandsOn({ slug: same.slug!, asset: same.asset! }); return; }
    // The defaults are DubaiOTC's accounts (TreasuryRebalance's rule). Cash
    // takes the fold (a currency has one spelling); the hot wallet takes the
    // EXACT code — one rail per chain, so USDT·ERC must not default onto TRC.
    if (entity === 'dubaiotc' && sameAsset(to, DEFAULT_RAILS.cash.asset)) { setLandsOn({ ...DEFAULT_RAILS.cash });   return; }
    if (entity === 'dubaiotc' && to === DEFAULT_RAILS.hotTrc.asset)       { setLandsOn({ ...DEFAULT_RAILS.hotTrc }); return; }   // dialect-ok: exact chain code — one rail per chain
    setLandsOn({ slug: '', asset: '' });
  }, [to, pairKnown]);   // eslint-disable-line react-hooks/exhaustive-deps

  // WHO CONVERTED IT — the party search Book trade uses, same query key, so
  // the list is a cache hit. Own accounts are not counterparties.
  const parties = useQuery({
    queryKey: ['parties', entity, partySlug.trim()],
    queryFn:  () => api.listParties({ entity, q: partySlug.trim() || undefined, with_balances: true }),
    enabled:  !selfConverts,
  });
  // A BALANCE LIST THE LEDGER COULD NOT GIVE comes back HTTP 200 with `balances_read: false` and `balances: {}` on every row — the
  // payload of a desk that owes nothing, which partyBoxWords prints as "square". An option says nothing about the box then
  // (a name is what this list is for; the box words are a courtesy, and a false "square" is not one) — and the list SAYS so, in
  // the one line a failed search gets (below): names kept, the way to read again offered.
  const boxesRead = !balancesUnread(parties.data);
  const partyOptions: ComboboxOption[] = useMemo(() =>
    (parties.data?.parties ?? [])
      .filter((p) => p.party_type !== 'own_account')
      .slice(0, 20)
      .map((p) => ({
        value:     p.slug,
        label:     partyName(p),
        secondary: [partySubtitle(p, { compact: true }), boxesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
        stale:     isStale(p),
        review:    needsNameReview(p.display_name),
      }))
  , [parties.data, boxesRead]);
  // Every party seen is remembered (NewTrade's merchantSlugs idea): the query
  // is re-keyed on the typed text, so `data` blinks undefined on each refetch
  // and the button must not blink with it. No lazy-create here — the
  // counterparty who changed the desk's money is somebody the books know.
  const knownParties = useRef<Map<string, string>>(new Map());
  const partyPicked = useMemo(() => {
    for (const p of parties.data?.parties ?? []) if (p.party_type !== 'own_account') knownParties.current.set(p.slug, partyName(p));
    return !!partySlug && knownParties.current.has(partySlug);
  }, [parties.data, partySlug]);

  // ON AN EXCHANGE the party is the exchange's own account row — the one
  // whose treasury slug is this account's. Read once per desk; the BFF
  // accepts that slug for a venue conversion and nothing else.
  const ownAccounts = useQuery({
    queryKey: ['parties', 'own-accounts', entity],
    queryFn:  () => api.listParties({ entity, type: 'own_account', pageSize: 500 }),
    enabled:  selfConverts,
  });
  const venueParty = useMemo(
    () => (selfConverts ? (ownAccounts.data?.parties ?? []).find((p) => p.treasury_slug === from.slug) ?? null : null),
    [selfConverts, ownAccounts.data, from.slug],
  );
  const partyExists = selfConverts ? !!venueParty : partyPicked;
  const partyLabel = selfConverts ? venueName : (knownParties.current.get(partySlug) ?? partySlug);
  const partyForPost = selfConverts ? (venueParty?.slug ?? '') : partySlug;

  // THE THREE BOXES — the shared calculator, as ConvertBalance renders them.
  const math = useTradeMath(from.asset, to);
  const fromText = math.amountOf(from.asset);
  const toText   = to ? math.amountOf(to) : '';
  const fromN = Number(fromText) || 0;
  const toN   = Number(toText)   || 0;
  const rateN = Number(math.rate) || 0;
  const fromWords = amountProblem(fromText);
  const toWords   = amountProblem(toText);
  const rateProblemWords = rateProblem(math.rate);
  const over = !unknown && fromN > balance ? fromN - balance : 0;
  const fromAuto = math.isDerived(from.asset);
  const toAuto   = !!to && math.isDerived(to);
  // Off-band — the shared table (lib/trade-math.ts, sweep item 24); warn,
  // never block. A derived rate says the amounts are what to check.
  const band = useMemo(() => {
    if (!pairKnown || rateProblemWords) return null;
    const w = bandWords(math.base, math.quote, rateN);
    return w;
  }, [pairKnown, rateN, rateProblemWords, math.base, math.quote]);
  // Its sentence is the table's and runs long: the first sentence is the amber line, the rest folds behind Details, and the
  // line stands under the three boxes — in a 230px Rate column it was five lines (22 Oct 2026 sweep). A rate the calculator
  // worked out from the two amounts says so on the line itself: then an amount is what is wrong, not the rate.
  const bandSaid = band ? sayFirst(band) : null;
  const bandDerived = math.derived === 'rate';

  // THE VENUE FEE (under More, exchange only). Entered = the figures above
  // are before the fee and the fee is recorded on its own; empty = whatever
  // the boxes say is what moved. Asset: one of the two legs'.
  const feeWords = optionalAmountProblem(venueFee);
  const feeN = Number(venueFee) || 0;
  const feeEntered = isExchange && venueFee.trim() !== '' && !feeWords && feeN > 0;

  // THE ACCOUNT THE FILLS QUERY ASKS FOR — not `venue` above, which is a
  // display word and, since the wind-down, ambiguous between two accounts at
  // one venue on one desk. `fromRow` is this rail's own treasury row (just
  // above), so `venue_account` is the BFF's resolved answer for exactly this
  // rail. Undefined until the treasury read lands, and undefined forever if
  // this exchange row's account could not be resolved — either way the query
  // below waits rather than ask an ambiguous question (found by Astra, 25
  // Sept 2026, verified against a live Bitget/Bitget·India pair; see the
  // field's own doc comment on TreasuryRow for the full account-mixing bug).
  const venueAccount = fromRow?.venue_account;

  // FROM THE EXCHANGE'S FILLS — the venue's own record of this pair over the
  // last 3 days, one button per day; a button presets the calculator, nothing
  // else moves.
  const fills = useQuery({
    queryKey: ['cex-fills', 'convert', entity, venueAccount?.venue, venueAccount?.label, FILLS_DAYS, pre.day],
    // after_conversion: every fill a conversion already booked on this venue
    // used is left out, so a run on the books is never offered again — in
    // whichever order the days are booked.
    queryFn:  () => api.cexFills({
      entity, venue: venueAccount!.venue, label: venueAccount!.label,
      from: fillsFrom(FILLS_DAYS, pre.day), limit: 1, afterConversion: true,
    }),
    enabled:  isExchange && pairKnown && !!venueAccount,
  });
  const pairFills = useMemo(
    () => (isExchange && pairKnown && fills.data ? fillsForPair(fills.data.summary, from.asset, to) : []),
    [isExchange, pairKnown, fills.data, from.asset, to],
  );
  // Every (pair, side) the venue filled in the window, oriented From → To
  // (the desk BOUGHT base with quote on 'buy', SOLD base for quote on 'sell').
  // `toCode` is the picker's own code for the pair's To side — set only when
  // this From asset is the pair's From side and the account holds it.
  const otherFills = useMemo(() => {
    if (!isExchange || !pairKnown || !fills.data) return [];
    return fills.data.summary.filter((g) => g.amount > 0).map((g) => {
      const [base = '', quote = ''] = g.symbol.split('/');
      const pairFrom = g.side === 'buy' ? quote : base;
      const pairTo   = g.side === 'buy' ? base  : quote;
      const pickable = ASSETS.find((a) => sameAsset(a, pairTo)) ?? null;
      const toCode = sameAsset(pairFrom, from.asset) && balance > 0 ? pickable : null;
      return { g, base, quote, pairFrom, toCode };
    });
  }, [isExchange, pairKnown, fills.data, from.asset, balance]);
  // The fills last applied, so a fee entered (or cleared) AFTER the tap
  // re-presets the figures the other way — inside or before the fee — and
  // the two can never double-count. Cleared when the pair changes.
  const [usedFills, setUsedFills] = useState<PairFills | null>(null);
  // The fee's asset: the one the operator picked, else the one the venue
  // charged in (the fills say — the tapped day's, else the first day's),
  // else the To asset.
  const feeAssetSaid = (usedFills ?? pairFills[0])?.feeAsset ?? null;
  const feeAssetPicked = venueFeeAsset
    || (feeAssetSaid ? (sameAsset(feeAssetSaid, from.asset) ? from.asset : to) : to);
  // What a day's tap would put in the From box, against what the account
  // holds — per entry, since each day is its own conversion.
  // DUST, OR A DEPOSIT NOT YET BOOKED? A venue that spent a hair more than the
  // books hold had dust (HTX, 15 Sept). A venue that spent 2,49,980 against a
  // book that holds nothing — or far more than it holds — was funded by money
  // that moved in and was never booked (Bitget, 16 Sept 2026): the fix is on
  // the Chain screen, not in the From box. Over one per cent of the preset,
  // or a book at zero, is the second case.
  const fillsCheck = (p: PairFills): { preset: number; over: number; unbooked: boolean } => {
    const feeInFrom = !!p.feeAsset && sameAsset(p.feeAsset, from.asset);
    const preset = p.fromAmount + (feeInFrom && !feeEntered ? p.fee : 0);
    const over = !unknown && preset > balance ? preset - balance : 0;
    return { preset, over, unbooked: over > 0 && (balance <= 0 || over > preset * 0.01) };
  };
  useEffect(() => { setUsedFills(null); }, [to, from.slug, from.asset]);
  const applyFills = useCallback((p: PairFills, beforeFee: boolean) => {
    const feeInFrom = !!p.feeAsset && sameAsset(p.feeAsset, from.asset);
    const feeInTo   = !!p.feeAsset && sameAsset(p.feeAsset, to);
    const fromAmt = beforeFee || !feeInFrom ? p.fromAmount : p.fromAmount + p.fee;
    const toAmt   = beforeFee || !feeInTo   ? p.toAmount   : p.toAmount - p.fee;
    math.preset({ asset: from.asset }, plain(fromAmt, from.asset));
    math.preset({ asset: to }, plain(toAmt, to));
    // The tap dates the conversion at that run's first fill too (operator, 15
    // Sept 2026, on chain rows: "time & date picker should auto filled to that
    // chain time … I don't need to select"). The chip row still shows it and
    // any chip changes it. Only on a NEW day: the fee re-preset re-runs this
    // for the same day and must not undo a date the operator then chose.
    if (usedFills?.g.day !== p.g.day && p.g.first_at) setWhen(isoToDubaiWall(p.g.first_at));
    setUsedFills(p);
  }, [math, from.asset, to, usedFills?.g.day]);
  // The opened day's fills, preset once when they arrive (a day with none presets nothing).
  const preDay = useRef(pre.day);
  useEffect(() => {
    const p = preDay.current ? pairFills.find((x) => x.g.day === preDay.current) : undefined;
    if (p) { preDay.current = null; applyFills(p, feeEntered); }
  }, [pairFills, applyFills, feeEntered]);
  useEffect(() => {
    if (usedFills) applyFills(usedFills, feeEntered);
  }, [feeEntered]);   // eslint-disable-line react-hooks/exhaustive-deps

  // THE SIDES, as the header explains: a currency for a coin is the one shape
  // the desk BUYS; every other pair the desk sells what it gives.
  const direction: Direction = isFiat(from.asset) && !isFiat(to) ? 'customer_sell' : 'customer_buy';
  const ready = !unknown && pairKnown && !!landsOn.slug && sameAsset(landsOn.asset, to) && partyExists
    && fromN > 0 && toN > 0 && rateN > 0 && !fromWords && !toWords && !rateProblemWords && !whenWords && !feeWords;

  // ── submit ───────────────────────────────────────────────────────────────
  // THE DEAL ID IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY (NewTrade's
  // rule): minted once, held in a ref, cleared on success, re-minted when the
  // day it carries changes and nothing landed. The confirmations are keyed by
  // WHICH guard refused — a settled trade that moves two accounts can meet the
  // twin-trade guard and the money-already-recorded guard in turn, and on an
  // exchange the "this account has never held BTC" door as well.
  const dealIdRef = useRef<string | null>(null);
  const confirmedRef = useRef<Set<RepeatKind | 'new_rail'>>(new Set());
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const [posted, setPosted] = useState<{
    at: number; dealId: string; deduped: boolean; asOf: string | null;
    from: string; to: string; fromRail: string; toRail: string; toSlug: string;
    fromAmount: number; toAmount: number; fromAfter: number; toAfter: number;
    /** The venue fee entered with the conversion, still to be recorded. */
    fee: { amount: number; asset: string; slug: string; venue: string } | null;
  } | null>(null);

  const post = async () => {
    if (!ready) throw new Error('nothing to convert');
    dealIdRef.current ??= `DEAL-${whenIso ? dubaiDate(whenIso) : dubaiDate()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    const dealId = dealIdRef.current;
    // Delivery follows the account the To asset lands on — the take side, as
    // Book trade derives it.
    const method = derivePaymentMethod(landsOn.slug);
    const landRow = rows.find((r) => r.slug === landsOn.slug && sameAsset(r.asset, landsOn.asset));
    const feeNote = feeEntered ? `venue fee ${plain(feeN, feeAssetPicked)} ${foldAsset(feeAssetPicked)}` : '';
    const data = await guard(() => api.bookTrade({
      entity,
      direction,
      party_slug: partyForPost,
      treasury_take_slug: landsOn.slug,
      treasury_give_slug: from.slug,
      delivery_method: method ? PM_TO_DM[method] : undefined,
      payment_method:  method,           // legacy field, kept for back-compat (as NewTrade)
      // The counterparty takes the From asset and gives the To asset. The
      // asset codes are the accounts' own, so the legs land where the books
      // already keep that coin.
      amount_take: { asset: landsOn.asset, amount: toN },
      amount_give: { asset: from.asset,    amount: fromN },
      rate:    math.rate.trim(),
      deal_id: dealId,
      // Marked so the Trades log tells a conversion of the desk's own money
      // from a customer's trade — and a venue's conversion from a merchant's.
      notes:   [isExchange ? 'venue conversion' : isBank ? 'bank conversion' : 'desk conversion', feeNote, notes.trim()].filter(Boolean).join(' · '),
      effective_at:              whenIso || undefined,
      // The first and the last fill the figures came from, so the next fills
      // read on this venue leaves that run out (only when the fills were used).
      fills_from:                isExchange && usedFills?.g.first_at ? usedFills.g.first_at : undefined,
      fills_until:               isExchange && usedFills?.g.last_at  ? usedFills.g.last_at  : undefined,
      confirm_repeat:            confirmedRef.current.has('repeat') || undefined,
      confirm_money_moved_again: confirmedRef.current.has('money')  || undefined,
      allow_new_rail_asset:      confirmedRef.current.has('new_rail') || undefined,
    }));
    // What the two accounts will read once the books answer — projected from
    // the balances this entry was typed against. The success line prefers
    // the LIVE figures once the balances have been re-read (see `posted.at`).
    return {
      data, dealId, asOf: whenIso || null,
      from: from.asset, to: landsOn.asset, fromRail: fromName, toRail: railName(landRow ?? { slug: landsOn.slug }), toSlug: landsOn.slug,
      fromAmount: fromN, toAmount: toN,
      fromAfter: balance - fromN, toAfter: (landRow?.balance ?? 0) + toN,
      fee: feeEntered ? { amount: feeN, asset: feeAssetPicked, slug: from.slug, venue: venueName } : null,
    };
  };
  const submit = useMutation({
    mutationFn: post,
    onSuccess: ({ data, dealId, ...rest }) => {
      clearTwin(); confirmedRef.current.clear();
      dealIdRef.current = null;
      setPosted({ at: Date.now(), dealId: data.deal_id ?? dealId, deduped: !!data.deduped, ...rest });
      // A clean slate for the next conversion; the counterparty stays — the
      // next one is often with the same money changer.
      setTo(''); math.reset(); setWhen(''); setNotes(''); setVenueFee(''); setVenueFeeAsset(''); setUsedFills(null);
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });
  const onWhenChange = (v: string) => {
    setWhen(v);
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) { dealIdRef.current = null; confirmedRef.current.clear(); clearTwin(); }
  };
  // AN ACCOUNT THAT HAS NEVER HELD THIS ASSET (400 new_rail_asset — the books
  // refuse to open one on their own; NewTrade's door, the same words). On an
  // exchange it is the ordinary first BTC on Bitget.
  const submitError: unknown = submit.error;
  const newRail = submit.isError && submitError instanceof ApiError && submitError.code === 'new_rail_asset'
    ? (() => {
        const body = submitError.body as { asset?: unknown; holds?: unknown };
        const asset = String(body.asset ?? '');
        const onFrom = sameAsset(asset, from.asset) && !sameAsset(asset, landsOn.asset);
        const slug = onFrom ? from.slug : landsOn.slug;
        return { asset: asset || landsOn.asset, slug, holds: Array.isArray(body.holds) ? body.holds.map(String) : [] };
      })()
    : null;

  // THE SCREEN IS REPLACED ONLY WHEN THERE IS NOTHING TO SHOW (Astra, 22 Oct, on the party form's twin): no read at all, or an
  // account at nought with no outcome to keep. Every posting refreshes the accounts; a refresh that failed used to unmount the
  // form — the outcome with it, "Fee to record" and the twin-trade and new-account doors among it.
  if (treasury.isError && (!treasury.data || (balance === 0 && !posted))) {
    return <ReadFailed what="the accounts" error={treasury.error} onRetry={() => void treasury.refetch()} tone="bad" />;
  }
  // Until the balances arrive `balance` reads 0 and the line below would call
  // a funded account empty on a cold deep link — so wait, as the party form does.
  if (treasury.isPending) return <Pending />;
  if (balance === 0 && !posted) {
    // ON AN EXCHANGE a zero on the books usually means the deposit was never
    // booked — the venue's own reading says so beside it, and the door is the
    // Chain screen, where the money that moved in is booked as a Move.
    if (isExchange) {
      const venueReading = typeof fromRow?.venue_balance === 'number' ? fromRow.venue_balance : null;
      // The panel's heading above names the account and its 0; the line says what the venue shows and the way to book it.
      return (
        <StateLine tone="warn" action={<Link to="/chain" className={TAP}>Book the money that moved in →</Link>}>
          {venueReading !== null ? `The venue shows ${fmtQuantity(venueReading, from.asset)} ${displayAsset(from.asset)} — nothing booked.` : 'Nothing booked on this account.'}
        </StateLine>
      );
    }
    return <EmptyState>Nothing to convert — no {displayAsset(from.asset)} held.</EmptyState>;
  }

  // The success line reads the LIVE balances once they have been re-read
  // after the posting (every mutation refreshes ['treasury']); until then,
  // the projection.
  const live = posted && treasury.dataUpdatedAt > posted.at;
  const liveOf = (slug: string, asset: string) => rows.find((r) => r.slug === slug && sameAsset(r.asset, asset))?.balance ?? 0;
  const fromAfter = posted ? (live ? liveOf(from.slug, posted.from) : posted.fromAfter) : 0;
  const toAfter   = posted ? (live ? liveOf(posted.toSlug, posted.to) : posted.toAfter) : 0;
  const treasuryHref = `/treasury${entity === 'dubaiotc' ? '' : `?entity=${entity}`}`;
  const feeHref = posted?.fee
    ? `/record/expense?rail=${encodeURIComponent(posted.fee.slug)}&railAsset=${encodeURIComponent(posted.fee.asset)}`
      + `&amount=${encodeURIComponent(plain(posted.fee.amount, posted.fee.asset))}&category=other`
      + `&note=${encodeURIComponent(`${posted.fee.venue} fee · ${posted.dealId}`)}`
    : '';

  return (
    <>
      {/* No From row (21 Oct 2026 audit): the panel's heading in Swap.tsx
          already says "Office cash · AED · 741,039.25", and the row repeated
          the account and its balance. The account stays the one selected. */}
      <Field label="To">
        <AssetPicker value={to} onChange={setTo} options={ASSETS} exclude={excludeFrom} />
        {chainSwap && (
          <p className="text-body lg:text-meta mt-1">
            Same coin on another chain — <Link to="/treasury/rebalance" className="btn-link text-meta">use Move money →</Link>
          </p>
        )}
      </Field>

      {!chainSwap && (
        <>
          {selfConverts && (
            // THE VENUE IS THE PARTY and the To asset stays on it — one line
            // says both; there is nothing to pick.
            <Field label="Party">
              <p className="text-body">Converted on {venueName}</p>
              {ownAccounts.isError && <ReadFailed what="the desk accounts" error={ownAccounts.error} onRetry={() => void ownAccounts.refetch()} className="mt-1" />}
              {ownAccounts.isSuccess && !venueParty && (
                <StateLine tone="bad" className="mt-1" action={<Link to="/parties/new" className={TAP}>Parties → New →</Link>} more="Open it from Parties → New → Own account.">
                  {venueName} has no own account on this desk.
                </StateLine>
              )}
            </Field>
          )}

          {/* THE REST OF THE FORM WAITS FOR TO (21 Oct 2026 audit: it opened
              with a Party box and three empty, disabled boxes before there
              was anything to convert into). Nothing that was enabled moves:
              none of it could act before To was chosen, and the button stays
              off until the whole conversion reads. */}
          {pairKnown && (
          <>
          {!selfConverts && (
            <FieldRow>
              <Field label="Lands on">
                <RailPicker
                  value={landsOn}
                  onChange={setLandsOn}
                  rows={landRows}
                  // The counterparty's own habits rank the list once chosen
                  // (12 Sept 2026) — the picker sits above the name box, so
                  // until then it is the desk's most used rails.
                  partySlug={partyExists ? partySlug : undefined}
                  partyEntity={entity}
                  allowedKinds={kindsForCategory(isFiat(to) ? 'fiat' : 'crypto')}
                  loading={treasury.isFetching}
                  readFailed={treasury.isError}
                  placeholder={`Account for ${displayAsset(to)}`}
                />
              </Field>

              <Field label="Party">
                <Combobox
                  value={partySlug}
                  onChange={setPartySlug}
                  options={partyOptions}
                  more={rosterCut(parties.data)}
                  placeholder="Search party"
                  loading={parties.isFetching || parties.fetchStatus === 'paused'}
                  // The failed line is INSIDE the open list (Combobox): below it the list covers it and Try again cannot be pressed. It is
                  // said for a search that FAILED and for one that came back 200 with `balances_read: false` (the names stand, the
                  // box words are gone) — the second used to be silent: ordinary names, no word, no way to read again.
                  readFailed={parties.isError
                    ? <ReadFailed what="the parties" error={parties.error} onRetry={() => void parties.refetch()} />
                    : boxesRead ? undefined
                    : <ReadFailed what="the balances" error={BALANCES_UNREAD} onRetry={() => void parties.refetch()} />}
                />
                {!parties.isError && partySlug.trim() && !partyExists && !parties.isFetching && (
                  <p className="text-meta text-tg-hint mt-1">Pick a party from the list.</p>
                )}
              </Field>
            </FieldRow>
          )}

          {/* FROM THE EXCHANGE'S FILLS — the venue's own record of this pair,
              one button per day, nothing automatic (header).
              22 Oct 2026 sweep: the panel's title is a Label (the panel sits inside the venue's own heading, so it does not
              name the venue again), a day's button says what it gives for what and nothing else — the rate, the fee and
              the count are the muted line under it — and the two guards under a day are ONE line each with their figures
              behind Details. The panel read as a wall of amber (27 and 31 words) beside every day. */}
          {isExchange && (
            <div className="rounded-lg bg-panel-2/60 p-3 space-y-2">
              <div className="flex items-baseline justify-between gap-3">
                <Label>Fills · {widened(FILLS_DAYS, pre.day) ? `since ${fmtDay(pre.day!)}` : `${FILLS_DAYS} days`}</Label>
                {fills.data && fills.data.booked > 0 && <span className="text-meta text-tg-hint">{fills.data.booked} already booked</span>}
              </div>
              {fills.isPending && <Pending inline />}
              {fills.isError && <ReadFailed what="the fills" error={fills.error} onRetry={() => void fills.refetch()} />}
              {fills.isSuccess && pairFills.length === 0 && <EmptyState>No fills for {displayAsset(from.asset)} → {displayAsset(to)}.</EmptyState>}
              {/* THE PAIRS THE VENUE DID FILL (16 Sept 2026: "fills option is
                  not rendering, only from usdt showing" — Bitget had bought
                  BTC with USDC while the form stood at USDT → USDC). One line
                  per (day, pair, side); a button sets the To asset only when
                  this From asset is the pair's From side — nothing else moves
                  — else the line names the account to pick on the left. The
                  fill count is not on these lines: it decides nothing here. */}
              {fills.isSuccess && pairFills.length === 0 && otherFills.map(({ g, base, quote, pairFrom, toCode }) => (
                <p key={`${g.day}|${g.symbol}|${g.side}`} className="text-body lg:text-meta">
                  {fmtDay(g.first_at ?? g.day)}{' · '}
                  {g.side === 'buy' ? 'Bought' : 'Sold'} {fmtQuantity(g.amount, base)} {displayAsset(base)}
                  {' '}{g.side === 'buy' ? 'with' : 'for'} {fmtAmount(g.cost, quote)} {displayAsset(quote)}
                  {toCode
                    ? <>{' '}<button type="button" className="btn-link text-meta" disabled={submit.isPending} onClick={() => setTo(toCode)}>To {displayAsset(toCode)}</button></>
                    : <span className="text-tg-hint"> — from {fromName} · {displayAsset(pairFrom)}</span>}
                </p>
              ))}
              {/* ONE BUTTON PER DAY, the day first; the fee note and the
                  over-balance check are that day's own. The button is one
                  line — the day and what it gives for what — and wraps on its
                  own words; at lg+ it is as wide as its words. */}
              {pairFills.map((p) => {
                const { preset, over, unbooked } = fillsCheck(p);
                return (
                  <div key={`${p.g.day}|${p.g.symbol}|${p.g.side}`} className="space-y-1.5">
                    <button
                      type="button"
                      className="btn-secondary text-body w-full lg:w-auto max-lg:text-left"
                      disabled={submit.isPending}
                      onClick={() => applyFills(p, feeEntered)}
                    >
                      {fmtDay(p.g.first_at ?? p.g.day)} · {fmtQuantity(p.toAmount, to)} {displayAsset(to)} for {fmtQuantity(p.fromAmount, from.asset)} {displayAsset(from.asset)}
                    </button>
                    <p className="text-meta text-tg-hint">
                      at {fillRate(p.g.rate)}
                      {' · '}fee {p.feeAsset ? `${fmtQuantity(p.fee, p.feeAsset)} ${displayAsset(p.feeAsset)}` : '—'}
                      {' · '}{p.g.fills} fill{p.g.fills === 1 ? '' : 's'}
                    </p>
                    {/* 21 Oct 2026 audit: the default sentence under every day —
                        "The fee stays inside the figures. To record it as a cost
                        on its own, enter it under More → Venue fee." — is in About
                        this screen (Swap.tsx) now, and the fill count rides on the
                        line under the button. Only the answer to a fee the operator
                        HAS entered stays here, where it changes what the figures mean. */}
                    {p.feeAsset && feeEntered && (
                      <p className="text-meta text-tg-hint">
                        Figures before the fee — it is booked on its own.
                      </p>
                    )}
                    {/* THE VENUE SOLD MORE THAN THE BOOKS HOLD (HTX, 15 Sept 2026:
                        49,990.0004 USDC sold against 49,990.00 deposited — the
                        0.0004 was dust the venue already had and the books never
                        carried). The preset would overdraw the account and the
                        post is refused; say so here, before the tap.
                        THE GUARD IS ONE LINE (22 Oct 2026): what is wrong and the next move; the books' figure and the
                        venue's behind Details, with the way to book the deposit one tap from the line. */}
                    {over > 0 && (
                      unbooked ? (
                        <StateLine
                          tone="warn"
                          more={<>The books hold {fmtAmount(balance, from.asset)} {displayAsset(from.asset)} on {fromName}; the venue spent {fmtQuantity(preset, from.asset)} {displayAsset(from.asset)}.</>}
                          action={<Link to="/chain" className={TAP}>Book it from Chain →</Link>}
                        >
                          The money that moved in is not booked yet.
                        </StateLine>
                      ) : (
                        <StateLine
                          tone="warn"
                          more={<>{venueName} sold {fmtQuantity(over, from.asset)} {displayAsset(from.asset)} more than {fromName} holds on the books ({fmtAmount(balance, from.asset)} {displayAsset(from.asset)}) — dust it already had. After the tap, trim the {displayAsset(from.asset)} box to {fmtAmount(balance, from.asset)}, or the post is refused.</>}
                        >
                          {fmtQuantity(over, from.asset)} {displayAsset(from.asset)} over the books — trim after the tap.
                        </StateLine>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* THREE LIVE BOXES — asset-1, asset-2 & rate, exactly as Convert
              balance renders them: each passes what was typed straight to the
              calculator, and the box it fills reads "auto" with its figure muted.

              THE PHONE (19 Sept 2026, the phone-layout pass — Convert
              balance's shape): the two amounts are the related pair, so they
              sit side by side — From amount → To amount, left to right — and
              the Rate takes the whole line beneath, where its hint and the
              rate-both-ways line have room. At lg+ the three columns are as
              they were. */}
          <FieldRow cols={3} className="max-lg:grid-cols-2">
            <Field label={`Amount in ${displayAsset(from.asset)}`} hint={fromAuto ? 'auto' : undefined}>
              <input
                className={`input${fromAuto ? ' text-tg-hint' : ''}`}
                value={fromText}
                onChange={(e) => math.setAmount(from.asset, e.target.value)}
                inputMode="decimal"
                disabled={!pairKnown}
              />
              <ErrorLine>{fromWords}</ErrorLine>
              {!fromWords && over > 0 && (
                <WarnLine>Over the balance by {fmtAmount(over, from.asset)}.</WarnLine>
              )}
            </Field>

            <Field label={to ? `Amount in ${displayAsset(to)}` : 'Amount it becomes'} hint={toAuto ? 'auto' : undefined}>
              <input
                className={`input${toAuto ? ' text-tg-hint' : ''}`}
                value={toText}
                onChange={(e) => { if (to) math.setAmount(to, e.target.value); }}
                inputMode="decimal"
                disabled={!pairKnown}
              />
              <ErrorLine>{toWords}</ErrorLine>
            </Field>

            <Field label="Rate" hint={pairKnown ? `${math.rateWords}${math.rateIsDerived ? ' · auto' : ''}` : undefined} className="max-lg:col-span-2">
              <input
                className={`input${math.rateIsDerived ? ' text-tg-hint' : ''}`}
                value={math.rate}
                onChange={(e) => math.setRate(e.target.value)}
                inputMode="decimal"
                disabled={!pairKnown}
              />
              <ErrorLine>{rateProblemWords}</ErrorLine>
              {!rateProblemWords && rateN > 0 && pairKnown && (
                <p className="text-meta text-tg-hint mt-1">
                  {/* each way is one piece: in the laptop's 230px Rate column the line breaks BETWEEN the two, never inside one */}
                  <span className="whitespace-nowrap">1 {displayAsset(math.base)} = {fmtRate(rateN, math.quote)} {displayAsset(math.quote)}</span>
                  {' · '}<span className="whitespace-nowrap">1 {displayAsset(math.quote)} = {fmtRate(1 / rateN, math.base)} {displayAsset(math.base)}</span>
                </p>
              )}
            </Field>
          </FieldRow>
          {bandSaid && (
            <StateLine tone="warn" more={bandSaid.rest || undefined}>
              {bandSaid.head}{bandDerived ? ' Worked out from the two amounts — check them.' : ''}
            </StateLine>
          )}

          {/* AS OF — the chip row every entry screen carries since 14 Sept
              2026 (sweep item 23); the chips render the echo and the
              back-date words themselves, onWhenChange keeps the deal id rule.
              After "Use the fills" the tapped day's first fill is one more
              chip, "From the fills" (header): the date moves only with that
              tap. The chip takes Dubai wall-clock, as the box does.
              (21 Oct 2026 audit: it sat alone in a two-column FieldRow, so its
              echo line floated mid-card; the chips stand on their own.) */}
          <AsOfChips
            value={when} onChange={onWhenChange} disabled={submit.isPending}
            preset={(usedFills?.g.first_at && isoToDubaiWall(usedFills.g.first_at)) || undefined}
            presetLabel="From the fills"
          />

          {/* Notes under More: 0 uses (§5.9). The venue fee sits with it on
              an exchange — optional, and its asset is one of the two legs'. */}
          <MoreDetails label="More">
            {isExchange && pairKnown && (
              <Field label="Venue fee" hint="optional">
                {/* The amount + asset pair side by side on every width; min-w-0
                    lets the amount box shrink beside the fixed-width select
                    inside the fold's indent at 375px (phone pass, 19 Sept 2026). */}
                <div className="flex gap-2">
                  <input
                    className="input flex-1 min-w-0"
                    value={venueFee}
                    onChange={(e) => setVenueFee(e.target.value)}
                    inputMode="decimal"
                    placeholder="0"
                  />
                  <select
                    className="select w-28 shrink-0"
                    value={feeAssetPicked}
                    onChange={(e) => setVenueFeeAsset(e.target.value)}
                  >
                    <option value={to}>{displayAsset(to)}</option>
                    <option value={from.asset}>{displayAsset(from.asset)}</option>
                  </select>
                </div>
                <ErrorLine>{feeWords}</ErrorLine>
              </Field>
            )}
            <Field label="Notes">
              <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
            </Field>
          </MoreDetails>

          {/* THE READ-BACK (§5.9, §7 rule 10): the trade direction and the "Lands on" default are derived and never shown,
              so one sentence says who takes what and gives what. 22 Oct 2026 sweep: the accounts are said ONCE each — the
              panel's heading is the From account and the Lands-on box is the To — so the sentence no longer names them
              again (it ran to 16 words, and to 23 beside a long party name), and for a venue or a bank, whose own name was
              in it twice ("Bitget converts … on Bitget"), it has no subject at all. */}
          {pairKnown && fromN > 0 && toN > 0 && (
            <p className="text-body lg:text-meta text-tg-text">
              {selfConverts ? (
                <>
                  Converts {fmtAmount(fromN, from.asset)} {displayAsset(from.asset)} into {fmtQuantity(toN, to)} {displayAsset(to)}
                  {feeEntered ? ` · venue fee ${fmtQuantity(feeN, feeAssetPicked)} ${displayAsset(feeAssetPicked)} booked separately` : ''}
                </>
              ) : (
                <>
                  {partyExists ? partyLabel : 'The merchant'} takes {fmtAmount(fromN, from.asset)} {displayAsset(from.asset)} and
                  gives {fmtAmount(toN, to)} {displayAsset(to)}
                </>
              )}
            </p>
          )}

          <div className="lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5">
            <button
              className="btn-primary w-full lg:w-auto lg:px-6"
              disabled={submit.isPending || !ready}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? 'Booking…'
                : `Convert${to ? ` to ${displayAsset(to)}` : ''}${whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : ''}`}
            </button>
          </div>
          </>
          )}
        </>
      )}

      {submit.isSuccess && posted && (
        posted.deduped ? (
          <StateLine
            tone="warn"
            more={<>Booked earlier under <span className="font-mono whitespace-nowrap">{posted.dealId}</span> — check its date in Trades.</>}
            action={<Link to="/trades" className={TAP}>Trades log →</Link>}
          >
            Already booked — nothing changed.
          </StateLine>
        ) : (
          // ONE LINE for what was booked; the date, the reference and the two doors are the muted line under it; the two
          // balances now are the next (22 Oct 2026 sweep — the line used to name each account beside its figure, twice).
          <div className="space-y-0.5">
            <p className="text-body text-pos">
              ✓ Converted {fmtAmount(posted.fromAmount, posted.from)} {displayAsset(posted.from)} → {fmtAmount(posted.toAmount, posted.to)} {displayAsset(posted.to)}
            </p>
            <p className="text-meta text-tg-hint">
              {posted.asOf ? `as of ${fmtDay(posted.asOf)} · ` : ''}<span className="font-mono whitespace-nowrap">{posted.dealId}</span>
              {' · '}<Link to="/trades" className={TAP}>Trades log →</Link>
              {' · '}<Link to={treasuryHref} className={TAP}>Wallets →</Link>
            </p>
            {/* The two balances NOW: the projection until the accounts are re-read, the live figure after. While the refresh is
                failed neither is claimed (the Convert list's banner says the accounts could not be read). */}
            {!unknown && (
              <p className="text-meta text-tg-hint">
                <span className="block">{posted.fromRail} now {fmtAmount(fromAfter, posted.from)} {displayAsset(posted.from)}</span>
                <span className="block">{posted.toRail} now {fmtAmount(toAfter, posted.to)} {displayAsset(posted.to)}</span>
              </p>
            )}
            {posted.fee && (
              <StateLine tone="warn" className="pt-1" action={<Link to={feeHref} className={TAP}>Record the fee →</Link>}>
                Fee to record: {fmtQuantity(posted.fee.amount, posted.fee.asset)} {displayAsset(posted.fee.asset)} from {posted.fromRail}.
              </StateLine>
            )}
          </div>
        )
      )}
      {submit.isError && !isSentinel(submit.error) && !newRail && <ServerLine error={submit.error} />}
      {/* The new-account door, in words (NewTrade's panel). Saying yes re-posts
          the same conversion, same deal id, with allow_new_rail_asset —
          remembered with the other yeses (confirmedRef). The line says what
          the books found; what Yes does is on its own button, and the rest
          ("check the asset and the account first") is behind Details. */}
      {newRail && (
        <div className="rounded-lg bg-warn-bg p-3 space-y-2">
          <StateLine
            tone="warn"
            more={<>
              {newRail.holds.length
                ? `It holds ${newRail.holds.map(displayAsset).join(' / ')} today. `
                : 'It has never been posted to. '}
              Yes opens {displayAsset(newRail.asset)} on this account and books the conversion there — check the asset and the account first.
            </>}
          >
            {railName(rows.find((r) => r.slug === newRail.slug) ?? { slug: newRail.slug })} has not held {displayAsset(newRail.asset)} before.
          </StateLine>
          <button
            type="button"
            // A long label: full width on the phone so it wraps inside its own
            // box, content-sized at lg+ as before (phone pass, 19 Sept 2026).
            className="btn-secondary text-body w-full lg:w-auto"
            disabled={submit.isPending || !!whenWords || unknown}
            onClick={() => { confirmedRef.current.add('new_rail'); submit.mutate(); }}
          >
            Yes — open {displayAsset(newRail.asset)} on {railName(rows.find((r) => r.slug === newRail.slug) ?? { slug: newRail.slug })} and book
          </button>
        </div>
      )}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          pending={submit.isPending || !!whenWords || unknown}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          // Confirm the guard that refused, and only that one (NewTrade's rule).
          onPostAnyway={() => { confirmedRef.current.add(repeat.kind); submit.mutate(); }}
        />
      )}
    </>
  );
}
